/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators';
import { cn } from '@/lib/utils';
import { getMarginBottomClass, getTextStyle } from '@/lib/page-designer/style-attributes';

const SEP = '· ·';
const DEFAULT_BACKGROUND_COLOR = '#1A1A1A';
const DEFAULT_TEXT_COLOR = '#FFFFFF';
const HEX_COLOR_REGEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

const parseHexColor = (value: string | undefined, fallback: string): string => {
    const trimmed = value?.trim();
    return trimmed && HEX_COLOR_REGEX.test(trimmed) ? trimmed : fallback;
};

@Component('scrollingBar', {
    name: 'Scrolling Bar',
    group: 'Content',
    description: 'Marquee ticker bar with up to 7 independently authored messages separated by decorative dividers',
})
@RegionDefinition([])
// oxlint-disable-next-line react/only-export-components
export class ScrollingBarMetadata {
    @AttributeDefinition({ name: 'Message 1', type: 'string' })
    message1!: string;

    @AttributeDefinition({ name: 'Message 2', type: 'string', required: false })
    message2?: string;

    @AttributeDefinition({ name: 'Message 3', type: 'string', required: false })
    message3?: string;

    @AttributeDefinition({ name: 'Message 4', type: 'string', required: false })
    message4?: string;

    @AttributeDefinition({ name: 'Message 5', type: 'string', required: false })
    message5?: string;

    @AttributeDefinition({ name: 'Message 6', type: 'string', required: false })
    message6?: string;

    @AttributeDefinition({ name: 'Message 7', type: 'string', required: false })
    message7?: string;

    @AttributeDefinition({
        id: 'backgroundColor',
        name: 'Background Color',
        description: 'Hex color for the bar background, e.g. #1A1A1A or #000.',
        type: 'string',
        defaultValue: DEFAULT_BACKGROUND_COLOR,
    })
    backgroundColor?: string;

    @AttributeDefinition({
        id: 'textColor',
        name: 'Text Color',
        description: 'Hex color for the message text and dividers, e.g. #FFFFFF or #fff.',
        type: 'string',
        defaultValue: DEFAULT_TEXT_COLOR,
    })
    textColor?: string;

    @AttributeDefinition({
        id: 'bodyFontStyle',
        name: 'Style – Message Font',
        type: 'enum',
        values: ['Default', 'Playfair Display', 'Inter'],
        defaultValue: 'Default',
    })
    bodyFontStyle?: string;

    @AttributeDefinition({
        id: 'bodyFontSize',
        name: 'Style – Message Size',
        type: 'enum',
        values: ['Default', 'Small', 'Medium', 'Large', 'X-Large'],
        defaultValue: 'Default',
    })
    bodyFontSize?: string;

    @AttributeDefinition({
        id: 'marginBottom',
        name: 'Style – Margin Bottom',
        type: 'enum',
        values: ['0', '2', '4', '6', '8', '12', '16'],
        defaultValue: '0',
        description: 'Space below the component (Tailwind spacing scale; 4 = 16px).',
    })
    marginBottom?: string;
}

interface ScrollingBarProps {
    message1: string;
    message2?: string;
    message3?: string;
    message4?: string;
    message5?: string;
    message6?: string;
    message7?: string;
    backgroundColor?: string;
    textColor?: string;
    bodyFontStyle?: string;
    bodyFontSize?: string;
    marginBottom?: string;
}

export default function ScrollingBar({
    message1,
    message2,
    message3,
    message4,
    message5,
    message6,
    message7,
    backgroundColor,
    textColor,
    bodyFontStyle,
    bodyFontSize,
    marginBottom,
}: ScrollingBarProps) {
    const items = [message1, message2, message3, message4, message5, message6, message7].filter(
        (m): m is string => typeof m === 'string' && m.trim().length > 0
    );

    if (items.length === 0) return null;

    // Three passes for a seamless marquee loop; each entry carries a stable per-pass key.
    const repeated = [0, 1, 2].flatMap((pass) => items.map((item) => ({ item, key: `${pass}-${item}` })));
    const colors = {
        backgroundColor: parseHexColor(backgroundColor, DEFAULT_BACKGROUND_COLOR),
        color: parseHexColor(textColor, DEFAULT_TEXT_COLOR),
    };

    const messageStyle = getTextStyle({ fontStyle: bodyFontStyle, fontSize: bodyFontSize }, 'body');

    return (
        <div
            className={cn('overflow-hidden py-3 select-none', getMarginBottomClass(marginBottom))}
            style={{ ...colors, ...messageStyle.style }}
            aria-hidden="true">
            <div className="flex animate-df-ticker whitespace-nowrap">
                {repeated.map(({ item, key }) => (
                    <span
                        key={key}
                        className={cn(
                            'inline-flex items-center gap-5 px-5 text-sm font-medium tracking-wide',
                            messageStyle.className
                        )}>
                        {item}
                        <span className="opacity-40">{SEP}</span>
                    </span>
                ))}
            </div>
        </div>
    );
}
